/**
 * 現場在庫の操作履歴テーブルの列定義
 *
 * InventoryPage の操作履歴モーダルで使う。列の表示（特に特記事項を省略しないこと）を
 * 単体でテストできるよう、ページから切り出している。
 */
import { Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatDateTimeShort } from '@/shared/utils/dateFormatter';
import { FullText } from '@/shared/components/Display/FullText';
import {
  type TransactionHistoryItem,
  INVENTORY_TRANSACTION_TYPE,
} from '@/features/inventory/types/inventory';

const { Text } = Typography;

/** 列幅（px） */
const COLUMN_WIDTH_PX = {
  CREATED_AT: 160,
  TRANSACTION_TYPE: 80,
  MATERIAL_TYPE_NAME: 120,
  QUANTITY: 80,
  QUANTITY_CHANGE: 120,
  REASON: 100,
  PERFORMED_BY: 100,
} as const;

/** 特記事項が未入力のときの表示 */
const EMPTY_NOTE_PLACEHOLDER = '-';

export const TRANSACTION_HISTORY_COLUMNS: ColumnsType<TransactionHistoryItem> = [
  {
    title: '日時',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: COLUMN_WIDTH_PX.CREATED_AT,
    render: (date: Date) => formatDateTimeShort(date)
  },
  {
    title: '種別',
    dataIndex: 'transactionType',
    key: 'transactionType',
    width: COLUMN_WIDTH_PX.TRANSACTION_TYPE,
    render: (type: string) => (
      <Tag color={type === INVENTORY_TRANSACTION_TYPE.INBOUND ? 'green' : 'orange'}>
        {type === INVENTORY_TRANSACTION_TYPE.INBOUND ? '入庫' : '消費'}
      </Tag>
    )
  },
  {
    title: '材料',
    dataIndex: 'materialTypeName',
    key: 'materialTypeName',
    width: COLUMN_WIDTH_PX.MATERIAL_TYPE_NAME
  },
  {
    title: '数量',
    dataIndex: 'quantity',
    key: 'quantity',
    width: COLUMN_WIDTH_PX.QUANTITY,
    align: 'right'
  },
  {
    title: '在庫推移',
    key: 'quantityChange',
    width: COLUMN_WIDTH_PX.QUANTITY_CHANGE,
    // dataIndex がない列では、antd は 1 つ目の引数に行のデータそのものを渡す
    render: (record: TransactionHistoryItem) => (
      <Text>
        {record.quantityBefore} → {record.quantityAfter}
      </Text>
    )
  },
  {
    title: '理由',
    dataIndex: 'reasonDisplayName',
    key: 'reasonDisplayName',
    width: COLUMN_WIDTH_PX.REASON
  },
  {
    title: '特記事項',
    dataIndex: 'note',
    key: 'note',
    // 省略すると全文をどこにも表示できないため、折り返して全文を表示する
    render: (note: string | null) => <FullText text={note} emptyPlaceholder={EMPTY_NOTE_PLACEHOLDER} />
  },
  {
    title: '操作者',
    dataIndex: 'performedByUserName',
    key: 'performedByUserName',
    width: COLUMN_WIDTH_PX.PERFORMED_BY
  }
];
